import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import HomePage from './components/HomePage';
import CategoryPage from './components/CategoryPage';
import Office365Page from './components/Office365Page';
import Office365PresentationPage from './components/Office365PresentationPage';
import GoogleWorkspacePage from './components/GoogleWorkspacePage';
import GoogleWorkspacePresentationPage from './components/GoogleWorkspacePresentationPage';
import SecurityPage from './components/SecurityPage';
import MobilePage from './components/MobilePage';
import SynologyPage from './components/SynologyPage';
import IntelligenceArtificiellePage from './components/IntelligenceArtificiellePage';
import OperatingSystemsPage from './components/OperatingSystemsPage';
import ArticlePage from './components/ArticlePage';
import AITypePage from './components/AITypePage';
import ApplePage from './components/ApplePage';
import AndroidPage from './components/AndroidPage';
import SearchResultsPage from './components/SearchResultsPage';
import AdminPage from './components/AdminPage';
import ContactPage from './components/ContactPage';
import { searchInContent, mockGuides } from './mock';

// Main App Content Component that uses Router hooks
const AppContent = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      const category = location.pathname.includes('/office365') ? 'office365' :
                     location.pathname.includes('/google-workspace') ? 'google-workspace' :
                     location.pathname.includes('/security') ? 'security' :
                     location.pathname.includes('/mobile') ? 'mobile' :
                     location.pathname.includes('/synology') ? 'synology' :
                     location.pathname.includes('/intelligence-artificielle') ? 'intelligence-artificielle' : null;
      
      const results = searchInContent(query, category);
      setSearchResults(results);
      
      // If searching from home page and results exist, go to the first result's category
      if (location.pathname === '/' && results.length > 0) {
        const firstResult = results[0];
        const categories = ['office365', 'google-workspace', 'security', 'mobile', 'synology', 'intelligence-artificielle'];
        
        for (const cat of categories) {
          const categoryGuides = searchInContent('', cat);
          if (categoryGuides.some(guide => guide.id === firstResult.id)) {
            navigate(`/${cat}`);
            break;
          }
        }
      }
    } else {
      setSearchResults([]);
    }
  };

  // Handle guide selection with URL navigation
  const handleGuideSelect = (item) => {
    console.log('Guide/Item selected:', item);
    if (item.type === 'article') {
      const currentCategory = location.pathname.split('/')[1] || 'office365';
      navigate(`/${currentCategory}/articles/${item.id}`);
    } else {
      const currentCategory = location.pathname.split('/')[1] || 'office365';
      navigate(`/${currentCategory}/guides/${item.id}`);
    }
  };

  return (
    <div className="App min-h-screen bg-gray-50">
      <Header />
      <Routes>
        <Route path="/" element={
          <HomePage 
            searchResults={searchResults} 
            searchQuery={searchQuery} 
            onSearch={handleSearch} 
          />
        } />
        
        <Route path="/office365" element={
          <Office365Page 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides.office365?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/office365/presentation" element={<Office365PresentationPage />} />
        
        <Route path="/google-workspace" element={
          <GoogleWorkspacePage 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides['google-workspace']?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/google-workspace/presentation" element={<GoogleWorkspacePresentationPage />} />
        
        <Route path="/security" element={
          <SecurityPage 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides.security?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/mobile" element={
          <MobilePage 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides.mobile?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/synology" element={
          <SynologyPage 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides.synology?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/intelligence-artificielle" element={
          <IntelligenceArtificiellePage 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides['intelligence-artificielle']?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/operating-systems" element={
          <OperatingSystemsPage 
            onSelectGuide={handleGuideSelect}
            guides={mockGuides['operating-systems']?.filter(guide => !guide.isSubPage) || []}
          />
        } />
        
        <Route path="/:category/guides/:guideId" element={<GuidePageWrapper />} />
        <Route path="/:category/articles/:articleId" element={<ArticlePageWrapper />} />
        <Route path="/ia/:aiType" element={<AITypePageWrapper />} />
        <Route path="/mobile/apple" element={<ApplePageWrapper />} />
        <Route path="/mobile/android" element={<AndroidPageWrapper />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Hidden Admin Route - Not visible on site */}
        <Route path="/MonCompte" element={<AdminPage />} />
      </Routes>
    </div>
  );
};

// Wrapper for Guide Pages with URL parameters
const GuidePageWrapper = () => {
  const { category, guideId } = useParams();
  const navigate = useNavigate();
  
  // Find the guide by ID
  const allGuides = mockGuides[category] || [];
  const guide = allGuides.find(g => g.id === guideId);
  
  console.log('GuidePageWrapper:', { category, guideId, allGuides: allGuides.length, guide: guide ? guide.title : 'NOT FOUND' });
  
  if (!guide) {
    console.log('Guide not found, redirecting to category');
    navigate(`/${category}`);
    return null;
  }
  
  return (
    <CategoryPage 
      category={category}
      searchResults={[]}
      searchQuery=""
      initialGuide={guide}
    />
  );
};

// Wrapper for Article Pages with URL parameters  
const ArticlePageWrapper = () => {
  const { category, articleId } = useParams();
  const navigate = useNavigate();
  
  // Find the article from the specialized pages
  const articles = {
    'office365': [
      {
        id: 'outlook-vs-exchange',
        title: 'Quelle différence entre Outlook et Exchange ?',
        description: 'Comprendre la distinction entre le client de messagerie et le serveur de messagerie Microsoft',
        date: '2024-09-20',
        readTime: '5 min',
        category: 'Fondamentaux',
        isNew: true,
        content: {
          introduction: 'Microsoft Outlook et Exchange Server sont deux composants distincts mais complémentaires de l\'écosystème de messagerie Microsoft. Cette confusion est fréquente, clarifions les rôles de chacun.',
          sections: [
            {
              title: '1. Microsoft Outlook : Le client de messagerie',
              content: '• **Définition** : Application installée sur votre ordinateur ou appareil mobile\n• **Fonction principale** : Interface utilisateur pour consulter, rédiger et organiser vos emails\n• **Versions disponibles** : Outlook Desktop, Outlook Web App, Outlook Mobile\n• **Utilisateurs** : Toute personne ayant besoin d\'accéder à ses emails',
              sectionColor: 'bg-blue-50 border-l-4 border-l-blue-500',
              sectionIcon: 'Mail',
              iconColor: 'text-blue-600'
            },
            {
              title: '2. Microsoft Exchange Server : Le serveur de messagerie',
              content: '• **Définition** : Serveur qui stocke et gère les boîtes aux lettres électroniques\n• **Fonction principale** : Infrastructure backend qui traite, stocke et distribue les emails\n• **Versions disponibles** : Exchange Server (on-premise), Exchange Online (cloud)\n• **Gestion** : Administrés par les équipes IT des entreprises',
              sectionColor: 'bg-green-50 border-l-4 border-l-green-500',
              sectionIcon: 'Server',
              iconColor: 'text-green-600'
            },
            {
              title: '3. Comment ils travaillent ensemble',
              content: '• **Connexion** : Outlook se connecte à Exchange pour récupérer les emails\n• **Synchronisation** : Exchange synchronise les emails sur tous vos appareils\n• **Sécurité** : Exchange gère la sécurité, Outlook affiche le contenu filtré\n• **Calendrier** : Exchange stocke les rendez-vous, Outlook les affiche',
              sectionColor: 'bg-purple-50 border-l-4 border-l-purple-500',
              sectionIcon: 'ArrowRightLeft',
              iconColor: 'text-purple-600'
            }
          ],
          conclusion: 'En résumé : Exchange est le "moteur" qui gère vos emails, Outlook est le "tableau de bord" qui vous permet de les consulter et les gérer.'
        }
      }
      // Add other articles here as needed
    ]
  };
  
  const article = articles[category]?.find(a => a.id === articleId);
  
  if (!article) {
    navigate(`/${category}`);
    return null;
  }
  
  return (
    <ArticlePage 
      article={article}
      currentSection={category}
      onBack={() => navigate(`/${category}`)}
    />
  );
};

// Wrapper for AI Type Pages with URL parameters  
const AITypePageWrapper = () => {
  const { aiType } = useParams();
  const navigate = useNavigate();
  
  return (
    <AITypePage 
      aiType={aiType}
      onBack={() => navigate('/')}
    />
  );
};

// Wrapper for Apple Pages
const ApplePageWrapper = () => {
  const navigate = useNavigate();
  
  return (
    <ApplePage 
      onBack={() => navigate('/')}
    />
  );
};

// Wrapper for Android Pages
const AndroidPageWrapper = () => {
  const navigate = useNavigate();
  
  return (
    <AndroidPage 
      onBack={() => navigate('/')}
    />
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;